import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV_LINKS } from "../data/content";

const Nav = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 border-b border-white/10 bg-nexa-black/60 backdrop-blur-xl" data-testid="main-nav">
        <div className="mx-auto flex h-16 max-w-[1600px] items-center justify-between px-5 md:px-10">
          <Link to="/" className="flex items-center" data-testid="nav-logo" aria-label="Nexa Studio home">
            <img src="/brand/wm-horizontal-red.svg" alt="Nexa Studio" width="57" height="28" className="h-7 w-auto" />
          </Link>

          <nav className="hidden items-center gap-7 lg:flex" aria-label="Primary">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.path}
                to={l.path}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `link-line font-mono text-[11px] uppercase tracking-[0.2em] transition-colors duration-200 ${
                    isActive ? "text-nexa-red" : "text-nexa-bone/80 hover:text-nexa-bone"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <a
              href="https://nexawears.in"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-link-nexa-wears"
              className="link-line inline-flex items-center gap-1 font-mono text-[11px] uppercase tracking-[0.2em] text-nexa-red transition-colors duration-200 hover:text-nexa-bone"
            >
              Nexa Wears <ArrowUpRight size={12} />
            </a>
            <Link
              to="/contact"
              data-testid="nav-cta-talk"
              className="bg-nexa-red px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] text-white transition-transform duration-200 hover:-translate-y-0.5 hover:bg-nexa-bone hover:text-nexa-black"
            >
              Let's Talk
            </Link>
          </nav>

          <button
            className="text-nexa-bone lg:hidden"
            onClick={() => setOpen(true)}
            aria-label="Open menu"
            data-testid="nav-menu-open"
          >
            <Menu size={26} />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[60] flex flex-col bg-nexa-red"
            initial={{ clipPath: "inset(0 0 100% 0)" }}
            animate={{ clipPath: "inset(0 0 0% 0)" }}
            exit={{ clipPath: "inset(0 0 100% 0)" }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            data-testid="mobile-menu"
          >
            <div className="flex h-16 items-center justify-between px-5">
              <img src="/brand/wm-horizontal-white.svg" alt="Nexa Studio" width="57" height="28" className="h-7 w-auto" />
              <button onClick={() => setOpen(false)} aria-label="Close menu" className="text-white" data-testid="nav-menu-close">
                <X size={28} />
              </button>
            </div>
            <nav className="flex flex-1 flex-col justify-center gap-1 px-6" aria-label="Mobile">
              {[...NAV_LINKS, { label: "Nexa Wears", path: "https://nexawears.in", external: true }, { label: "Contact", path: "/contact" }].map((l, i) => (
                <motion.div
                  key={l.path}
                  initial={{ x: -40, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.15 + i * 0.05 }}
                >
                  {l.external ? (
                    <a
                      href={l.path}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setOpen(false)}
                      data-testid="mobile-nav-nexa-wears"
                      className="inline-flex items-center gap-2 font-display text-5xl uppercase leading-[1.05] text-nexa-black"
                    >
                      {l.label} <ArrowUpRight size={36} />
                    </a>
                  ) : (
                    <Link
                      to={l.path}
                      onClick={() => setOpen(false)}
                      data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                      className={`font-display text-5xl uppercase leading-[1.05] text-white ${
                        location.pathname === l.path ? "text-nexa-black" : ""
                      }`}
                    >
                      {l.label}
                    </Link>
                  )}
                </motion.div>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Nav;
